import axios, { AxiosResponse } from 'axios';
import {
  AuthResponse,
  LoginCredentials,
  RegisterData,
  User,
  Course,
  Lesson,
  Enrollment
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://e-learning-slar.onrender.com/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/auth/token/refresh/`, {
            refresh: refreshToken,
          });
          
          localStorage.setItem('access_token', response.data.access);
          api.defaults.headers.Authorization = `Bearer ${response.data.access}`;
          
          return api(originalRequest);
        } catch (refreshError) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user');
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      }
    }
    
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    const response: AxiosResponse<AuthResponse> = await api.post('/auth/login/', credentials);
    return response.data;
  },

  register: async (data: RegisterData): Promise<AuthResponse> => {
    const response: AxiosResponse<AuthResponse> = await api.post('/auth/register/', data);
    return response.data;
  },

  logout: async (): Promise<void> => {
    const refreshToken = localStorage.getItem('refresh_token');
    if (refreshToken) {
      await api.post('/auth/logout/', { refresh_token: refreshToken });
    }
  },

  getProfile: async (): Promise<User> => {
    const response: AxiosResponse<User> = await api.get('/auth/profile/');
    return response.data;
  },

  updateProfile: async (data: any): Promise<User> => {
    const response: AxiosResponse<User> = await api.patch('/auth/profile/', data);
    return response.data;
  },
};

// Courses API
export const coursesAPI = {
  getAll: async (params?: {
    category?: string;
    difficulty?: string;
    search?: string;
    page?: number;
  }): Promise<{ results: Course[]; count: number; next?: string; previous?: string }> => {
    const response = await api.get('/courses/', { params });
    return response.data;
  },

  getById: async (id: number): Promise<Course> => {
    const response: AxiosResponse<Course> = await api.get(`/courses/${id}/`);
    return response.data;
  },

  create: async (data: Partial<Course> | FormData): Promise<Course> => {
    const response: AxiosResponse<Course> = await api.post('/courses/', data, {
      headers: data instanceof FormData ? {
        'Content-Type': 'multipart/form-data',
      } : {
        'Content-Type': 'application/json',
      }
    });
    return response.data;
  },

  update: async (id: number, data: Partial<Course>): Promise<Course> => {
    const response: AxiosResponse<Course> = await api.patch(`/courses/${id}/`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/courses/${id}/`);
  },

  enroll: async (courseId: number): Promise<Enrollment> => {
    const response: AxiosResponse<Enrollment> = await api.post(`/courses/${courseId}/enroll/`);
    return response.data;
  },

  getInstructorCourses: async (): Promise<Course[]> => {
    const response: AxiosResponse<Course[]> = await api.get('/instructor/courses/');
    return response.data;
  },
};

// Lessons API
export const lessonsAPI = {
  getByCourse: async (courseId: number): Promise<Lesson[]> => {
    const response: AxiosResponse<Lesson[]> = await api.get(`/courses/${courseId}/lessons/`);
    return response.data;
  },

  create: async (courseId: number, data: Partial<Lesson>): Promise<Lesson> => {
    const response: AxiosResponse<Lesson> = await api.post(`/courses/${courseId}/lessons/`, data);
    return response.data;
  },

  update: async (id: number, data: Partial<Lesson>): Promise<Lesson> => {
    const response: AxiosResponse<Lesson> = await api.patch(`/lessons/${id}/`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/lessons/${id}/`);
  },
};

// Enrollments API
export const enrollmentsAPI = {
  getAll: async (): Promise<{ results: Enrollment[]; count: number }> => {
    const response = await api.get('/enrollments/');
    return response.data;
  },

  updateProgress: async (id: number, progress: number): Promise<Enrollment> => {
    const response: AxiosResponse<Enrollment> = await api.patch(`/enrollments/${id}/progress/`, {
      progress,
    });
    return response.data;
  },
};

export default api;
